import React from "react";
import useHoverTooltip from "./useHoverTooltip";
import "./EllipsisText.css";

/**
 * Shows a tooltip while its content is hovered, including a disabled button, which gets no hover of its own.
 * `text` may be a string or an element; `label` names it for assistive technology when it is not a string.
 * An interactive tooltip stays open while the pointer is over it, so it can be scrolled; the arrow keys scroll it
 * while the content has focus.
 */
export default function HoverTooltip({text, label, interactive = false, children}) {
    const {show, hide, scrollWithKeys, tooltip} = useHoverTooltip(text, {interactive});
    if (!text) {
        return children;
    }
    // A disabled button cannot take focus, so the hint doubles as its accessible name.
    const accessibleName = label ?? (typeof text === "string" ? text : undefined);
    const labelProp = typeof children?.type === "string" ? "aria-label" : "ariaLabel";
    const content = accessibleName && React.isValidElement(children) && children.props[labelProp] === undefined
        ? React.cloneElement(children, {[labelProp]: accessibleName})
        : children;

    return (
        <>
            <span className="hoverTooltipTarget" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}
                  onKeyDown={interactive ? scrollWithKeys : undefined}>
                {content}
            </span>
            {tooltip}
        </>
    );
}
